import React, { useState, useRef } from 'react';
import { Card, Form, Input, Button, message, Spin, Row, Col, Tag, Avatar, Divider, Alert, Radio, Result, Table, Progress, Badge, Segmented, AutoComplete } from 'antd';
import { InstagramOutlined, UserAddOutlined, CheckCircleOutlined, LinkOutlined, UserOutlined, TeamOutlined, PictureOutlined, TagOutlined, UploadOutlined, PauseCircleOutlined, PlayCircleOutlined } from '@ant-design/icons';
import apiClient from '../../server/server';

const AIQuickProfileCreator = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [createdProfile, setCreatedProfile] = useState(null);
  const [error, setError] = useState(null);
  const [cityName, setCityName] = useState('Miami');
  const [mode, setMode] = useState('single'); // 'single' or 'batch'

  // Batch state
  const [batchInput, setBatchInput] = useState('');
  const [batchItems, setBatchItems] = useState([]);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProfileType, setBatchProfileType] = useState('EM');
  const stopRef = useRef(false);

  // Quick city suggestions (user can type any city)
  const citySuggestions = [
    'Miami', 'Toronto', 'Montreal', 'Tunis', 'New York City', 'Los Angeles',
    'San Francisco', 'Chicago', 'Detroit', 'Berlin', 'London', 'Paris',
    'Amsterdam', 'Barcelona', 'Ibiza', 'Mexico City', 'Bogota', 'Sao Paulo',
    'Tokyo', 'Seoul', 'Sydney', 'Melbourne', 'Cape Town', 'Dubai'
  ];

  // Get user ID from localStorage
  const getUserId = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    return user?._id || null;
  };

  // Extract Instagram handle from URL or handle
  const extractInstagramHandle = (input) => {
    if (!input) return null;
    // Remove @ if present
    let handle = input.trim().replace(/^@/, '');
    // Extract from URL if it's a URL
    const urlMatch = handle.match(/instagram\.com\/([^/?]+)/i);
    if (urlMatch) {
      handle = urlMatch[1];
    }
    return handle;
  };

  // Parse pasted Excel/TSV data into batch items
  const parseBatchInput = (text) => {
    const lines = text.trim().split('\n').filter(line => line.trim());
    const parsed = lines.map((line, index) => {
      const cols = line.split('\t').map(c => c.trim());
      // Expected: Name | RA URL | @handle | Instagram URL | Status
      // But be flexible — find the instagram handle and RA URL from whatever columns exist
      let name = '';
      let scrapSource = '';
      let instagramHandle = '';

      for (const col of cols) {
        if (col.match(/instagram\.com\//i)) {
          // Full instagram URL — extract handle
          const match = col.match(/instagram\.com\/([^/?]+)/i);
          if (match) instagramHandle = match[1];
        } else if (col.startsWith('@')) {
          instagramHandle = col.replace(/^@/, '');
        } else if (col.match(/ra\.co\/clubs\//i) || col.match(/ra\.co\/club\//i)) {
          scrapSource = col;
        } else if (col === 'Verified' || col === 'Not Verified') {
          // skip status column
        } else if (!name && col.length > 0) {
          name = col;
        }
      }

      return {
        key: index,
        name,
        instagramHandle,
        scrapSource,
        status: 'pending', // pending | processing | success | error
        message: ''
      };
    }).filter(item => item.instagramHandle); // only keep rows with a handle

    return parsed;
  };

  const handleParseBatch = () => {
    const items = parseBatchInput(batchInput);
    if (items.length === 0) {
      message.warning('No valid rows found. Each row needs at least an Instagram handle.');
      return;
    }
    setBatchItems(items);
    message.success(`Parsed ${items.length} profiles ready to create`);
  };

  const handleRunBatch = async () => {
    const userId = getUserId();
    if (!userId) {
      message.error('User ID not found. Please log in again.');
      return;
    }

    setBatchRunning(true);
    stopRef.current = false;

    const updatedItems = [...batchItems];

    for (let i = 0; i < updatedItems.length; i++) {
      if (stopRef.current) {
        message.info('Batch stopped by user');
        break;
      }

      const item = updatedItems[i];
      if (item.status === 'success') continue; // skip already done

      // Mark processing
      updatedItems[i] = { ...item, status: 'processing', message: '' };
      setBatchItems([...updatedItems]);

      try {
        const requestBody = {
          instagramHandle: item.instagramHandle,
          profileType: batchProfileType,
          cityName: cityName,
          userId
        };
        if (item.name) requestBody.name = item.name;
        if (item.scrapSource) requestBody.scrapSource = item.scrapSource;

        const response = await apiClient.post('/ai/create-profile', requestBody);

        if (response.data?.success) {
          updatedItems[i] = { ...updatedItems[i], status: 'success', message: response.data.message || 'Created' };
        } else {
          throw new Error(response.data?.message || 'Failed');
        }
      } catch (err) {
        const errorMsg = err.response?.data?.message || err.message || 'Failed';
        updatedItems[i] = { ...updatedItems[i], status: 'error', message: errorMsg };
      }

      setBatchItems([...updatedItems]);
    }

    setBatchRunning(false);
  };

  const handleStopBatch = () => {
    stopRef.current = true;
  };

  const handleResetBatch = () => {
    setBatchItems([]);
    setBatchInput('');
    stopRef.current = false;
  };

  // Create profile using the endpoint
  const handleCreateProfile = async (values) => {
    const instagramHandle = extractInstagramHandle(values.instagramHandle);
    if (!instagramHandle) {
      message.warning('Please enter an Instagram handle or URL');
      return;
    }

    const userId = getUserId();
    if (!userId) {
      message.error('User ID not found. Please log in again.');
      return;
    }

    setLoading(true);
    setCreatedProfile(null);
    setError(null);

    try {
      const requestBody = {
        instagramHandle: instagramHandle,
        profileType: values.profileType,
        userId: userId
      };

      // Add optional overrides if provided
      if (values.name?.trim()) {
        requestBody.name = values.name.trim();
      }
      if (values.description?.trim()) {
        requestBody.description = values.description.trim();
      }
      // Add picture URL if provided (skips Instagram scraping)
      if (values.pictureUrl?.trim()) {
        requestBody.pictureUrl = values.pictureUrl.trim();
      }
      // Add scrap source if provided (e.g. RA club URL)
      if (values.scrapSource?.trim()) {
        requestBody.scrapSource = values.scrapSource.trim();
      }

      requestBody.cityName = cityName;
      console.log(`Creating ${cityName} profile:`, requestBody);

      const response = await apiClient.post('/ai/create-profile', requestBody);

      if (response.data?.success) {
        setCreatedProfile(response.data);
        message.success(response.data.message || 'Profile created successfully!');
        form.resetFields();
      } else {
        throw new Error(response.data?.message || 'Failed to create profile');
      }
    } catch (err) {
      console.error('Profile creation error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to create profile';
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Reset to create another profile
  const handleCreateAnother = () => {
    setCreatedProfile(null);
    setError(null);
    form.resetFields();
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <Card
        title={<><InstagramOutlined /> Create Profile from Instagram</>}
        extra={
          <Segmented
            options={[
              { label: 'Single', value: 'single' },
              { label: 'Batch Import', value: 'batch' }
            ]}
            value={mode}
            onChange={setMode}
          />
        }
        style={{ marginBottom: 24 }}
      >
        <Alert
          message={`${cityName} Profile Creator${mode === 'batch' ? ' — Batch Mode' : ''}`}
          description={mode === 'batch'
            ? `Paste rows from Excel (tab-separated). Each row should have: Name, RA URL, @handle, Instagram URL. Profiles will be created one by one for ${cityName}.`
            : `Enter an Instagram handle to automatically create a ${cityName}-based profile with AI-generated bio, music genres, and full HD profile picture uploaded to S3.`
          }
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        {/* ====== BATCH MODE ====== */}
        {mode === 'batch' && (
          <>
            {/* City selector for batch */}
            <Form.Item label="City" required style={{ marginBottom: 16 }}>
              <AutoComplete
                value={cityName}
                onChange={setCityName}
                options={citySuggestions.filter(c => c.toLowerCase().includes((cityName || '').toLowerCase())).map(c => ({ value: c }))}
                placeholder="Type a city name..."
                size="large"
                style={{ width: 300 }}
                disabled={batchRunning}
              />
            </Form.Item>

            <Form.Item label="Profile Type" required style={{ marginBottom: 16 }}>
              <Radio.Group
                size="large"
                value={batchProfileType}
                onChange={(e) => setBatchProfileType(e.target.value)}
                disabled={batchRunning}
              >
                <Radio.Button value="M"><UserOutlined /> DJ / Artist</Radio.Button>
                <Radio.Button value="EM"><TeamOutlined /> Event Manager / Collective</Radio.Button>
                <Radio.Button value="L"><TagOutlined /> Label</Radio.Button>
              </Radio.Group>
            </Form.Item>

            {/* Paste area */}
            {batchItems.length === 0 && (
              <>
                <Input.TextArea
                  rows={10}
                  placeholder={"Paste rows from Excel here (tab-separated):\nBASEMENT\thttps://fr.ra.co/clubs/165976\t@basementnewyork\thttps://www.instagram.com/basementnewyork/\tVerified\nNowdays\thttps://fr.ra.co/clubs/105873\t@nowadaysnyc\thttps://www.instagram.com/nowadaysnyc/\tVerified"}
                  value={batchInput}
                  onChange={(e) => setBatchInput(e.target.value)}
                  style={{ marginBottom: 16, fontFamily: 'monospace', fontSize: 12 }}
                />
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  onClick={handleParseBatch}
                  disabled={!batchInput.trim()}
                  size="large"
                  block
                >
                  Parse & Preview
                </Button>
              </>
            )}

            {/* Preview table + controls */}
            {batchItems.length > 0 && (
              <>
                {/* Progress summary */}
                <div style={{ marginBottom: 16 }}>
                  <Progress
                    percent={Math.round((batchItems.filter(i => i.status === 'success' || i.status === 'error').length / batchItems.length) * 100)}
                    status={batchItems.some(i => i.status === 'error') ? 'exception' : undefined}
                  />
                  <Row gutter={16} style={{ marginTop: 8 }}>
                    <Col><Badge status="default" text={`${batchItems.filter(i => i.status === 'pending').length} pending`} /></Col>
                    <Col><Badge status="processing" text={`${batchItems.filter(i => i.status === 'processing').length} processing`} /></Col>
                    <Col><Badge status="success" text={`${batchItems.filter(i => i.status === 'success').length} success`} /></Col>
                    <Col><Badge status="error" text={`${batchItems.filter(i => i.status === 'error').length} failed`} /></Col>
                  </Row>
                </div>

                <Table
                  dataSource={batchItems}
                  size="small"
                  pagination={false}
                  scroll={{ y: 400 }}
                  columns={[
                    {
                      title: '#',
                      dataIndex: 'key',
                      width: 40,
                      render: (v) => v + 1
                    },
                    {
                      title: 'Name',
                      dataIndex: 'name',
                      width: 160,
                      ellipsis: true
                    },
                    {
                      title: 'Instagram',
                      dataIndex: 'instagramHandle',
                      width: 160,
                      render: (v) => <Tag icon={<InstagramOutlined />}>@{v}</Tag>
                    },
                    {
                      title: 'Scrap Source',
                      dataIndex: 'scrapSource',
                      width: 180,
                      ellipsis: true,
                      render: (v) => v ? <a href={v} target="_blank" rel="noopener noreferrer">{v.replace(/https?:\/\/(fr\.)?/, '')}</a> : '-'
                    },
                    {
                      title: 'Status',
                      dataIndex: 'status',
                      width: 120,
                      render: (status, record) => {
                        if (status === 'pending') return <Badge status="default" text="Pending" />;
                        if (status === 'processing') return <Badge status="processing" text="Processing..." />;
                        if (status === 'success') return <Badge status="success" text="Created" />;
                        if (status === 'error') return <Badge status="error" text={record.message || 'Failed'} />;
                        return null;
                      }
                    }
                  ]}
                  style={{ marginBottom: 16 }}
                />

                <Row gutter={12}>
                  {!batchRunning ? (
                    <>
                      <Col>
                        <Button
                          type="primary"
                          icon={<PlayCircleOutlined />}
                          onClick={handleRunBatch}
                          size="large"
                        >
                          {batchItems.some(i => i.status === 'success' || i.status === 'error')
                            ? 'Resume Batch'
                            : `Start Batch (${batchItems.length} profiles)`
                          }
                        </Button>
                      </Col>
                      <Col>
                        <Button onClick={handleResetBatch} size="large">
                          Clear & Start Over
                        </Button>
                      </Col>
                    </>
                  ) : (
                    <Col>
                      <Button
                        danger
                        icon={<PauseCircleOutlined />}
                        onClick={handleStopBatch}
                        size="large"
                      >
                        Stop Batch
                      </Button>
                    </Col>
                  )}
                </Row>
              </>
            )}
          </>
        )}

        {/* ====== SINGLE MODE ====== */}
        {mode === 'single' && <>
        {/* Success Result */}
        {createdProfile && (
          <Result
            status="success"
            title="Profile Created Successfully!"
            subTitle={createdProfile.message}
            extra={[
              <Button type="primary" key="another" onClick={handleCreateAnother}>
                Create Another Profile
              </Button>,
              <Button
                key="view"
                onClick={() => window.open(`/profile-detail/${createdProfile.profile?.id}`, '_blank')}
              >
                View Profile
              </Button>
            ]}
          >
            <div style={{ textAlign: 'left', background: '#f5f5f5', padding: 24, borderRadius: 8 }}>
              <Row gutter={16} align="middle">
                <Col>
                  <Avatar size={80} src={createdProfile.profile?.picture}>
                    {createdProfile.profile?.username?.[0]}
                  </Avatar>
                </Col>
                <Col flex={1}>
                  <h3 style={{ margin: 0 }}>{createdProfile.profile?.username}</h3>
                  <p style={{ margin: '4px 0', color: '#666' }}>
                    {createdProfile.profile?.subType} - {createdProfile.profile?.city}
                  </p>
                  <Tag color="success">{createdProfile.profile?.profileType === 'M' ? 'DJ / Artist' : createdProfile.profile?.profileType === 'L' ? 'Label' : 'Event Manager'}</Tag>
                </Col>
              </Row>

              {createdProfile.profile?.musicGenres?.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <strong>Music Genres: </strong>
                  {createdProfile.profile.musicGenres.map((genre, idx) => (
                    <Tag key={idx} color="blue">{genre}</Tag>
                  ))}
                </div>
              )}

              {createdProfile.profile?.bio && (
                <div style={{ marginTop: 12 }}>
                  <strong>Bio:</strong>
                  <p style={{ marginTop: 4, color: '#666' }}>{createdProfile.profile.bio}</p>
                </div>
              )}

              <div style={{ marginTop: 12 }}>
                <a
                  href={createdProfile.profile?.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Tag icon={<InstagramOutlined />} color="purple">
                    @{createdProfile.instagramHandle}
                  </Tag>
                </a>
              </div>
            </div>
          </Result>
        )}

        {/* Error Display */}
        {error && !createdProfile && (
          <Alert
            message="Profile Creation Failed"
            description={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
            style={{ marginBottom: 24 }}
          />
        )}

        {/* Form - Only show when not showing success result */}
        {!createdProfile && (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleCreateProfile}
            initialValues={{
              profileType: 'M'
            }}
          >
            <Form.Item
              label="City"
              required
            >
              <AutoComplete
                value={cityName}
                onChange={setCityName}
                options={citySuggestions.filter(c => c.toLowerCase().includes((cityName || '').toLowerCase())).map(c => ({ value: c }))}
                placeholder="Type a city name..."
                size="large"
                style={{ width: 300 }}
                disabled={loading}
              />
            </Form.Item>

            <Form.Item
              name="instagramHandle"
              label="Instagram Handle or URL"
              rules={[{ required: true, message: 'Please enter an Instagram handle' }]}
            >
              <Input
                placeholder="@ms_mada_ or https://instagram.com/ms_mada_"
                size="large"
                prefix={<InstagramOutlined />}
                disabled={loading}
              />
            </Form.Item>

            <Form.Item
              name="profileType"
              label="Profile Type"
              rules={[{ required: true, message: 'Please select a profile type' }]}
            >
              <Radio.Group size="large" disabled={loading}>
                <Radio.Button value="M">
                  <UserOutlined /> DJ / Artist
                </Radio.Button>
                <Radio.Button value="EM">
                  <TeamOutlined /> Event Manager / Collective
                </Radio.Button>
                <Radio.Button value="L">
                  <TagOutlined /> Label
                </Radio.Button>
              </Radio.Group>
            </Form.Item>

            <Divider>Optional Overrides</Divider>

            <Form.Item
              name="pictureUrl"
              label="Picture URL (skip Instagram scraping)"
            >
              <Input
                placeholder="Paste direct image URL if Instagram scraping fails"
                prefix={<PictureOutlined />}
                disabled={loading}
              />
            </Form.Item>

            <Form.Item
              name="scrapSource"
              label="Scrap Source URL (e.g. RA club page)"
            >
              <Input
                placeholder="https://ra.co/clubs/165976"
                prefix={<LinkOutlined />}
                disabled={loading}
              />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label="Name (override AI)"
                >
                  <Input
                    placeholder="Leave empty to use AI-generated name"
                    disabled={loading}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="description"
                  label="Bio (override AI)"
                >
                  <Input.TextArea
                    placeholder="Leave empty to use AI-generated bio (280-300 chars)"
                    rows={3}
                    disabled={loading}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                icon={<UserAddOutlined />}
                loading={loading}
                size="large"
                block
              >
                {loading ? 'Creating Profile...' : `Create ${cityName} Profile`}
              </Button>
            </Form.Item>

            {loading && (
              <div style={{ textAlign: 'center', padding: 20 }}>
                <Spin size="large" />
                <p style={{ marginTop: 16, color: '#666' }}>
                  Fetching Instagram profile picture, generating AI bio, and uploading to S3...
                </p>
              </div>
            )}
          </Form>
        )}

        {/* Info about what happens */}
        {!createdProfile && !loading && (
          <Alert
            message="What happens when you create a profile:"
            description={
              <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                <li>Instagram profile picture is scraped and converted to full HD (or use Picture URL if scraping fails)</li>
                <li>Image is uploaded to S3 (decadanseapp bucket)</li>
                <li>AI generates professional name, bio (280-300 chars), and 2-3 music genres</li>
                <li>Profile is auto-verified and assigned to {cityName}</li>
              </ul>
            }
            type="warning"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
        </>}
      </Card>
    </div>
  );
};

export default AIQuickProfileCreator;
